import { useState, useEffect, useRef } from 'react';
import { Input } from './input';
import { ChevronDown, CheckCircle2, AlertCircle } from 'lucide-react';

const COUNTRY_CODES = [
  { code: '+41', country: 'CH', flag: '🇨🇭', label: 'Schweiz', digits: [9, 9] },
  { code: '+49', country: 'DE', flag: '🇩🇪', label: 'Deutschland', digits: [10, 11] },
  { code: '+43', country: 'AT', flag: '🇦🇹', label: 'Österreich', digits: [10, 13] },
  { code: '+39', country: 'IT', flag: '🇮🇹', label: 'Italien', digits: [9, 11] },
  { code: '+33', country: 'FR', flag: '🇫🇷', label: 'Frankreich', digits: [9, 9] },
  { code: '+44', country: 'GB', flag: '🇬🇧', label: 'UK', digits: [10, 10] },
  { code: '+1', country: 'US', flag: '🇺🇸', label: 'USA/Kanada', digits: [10, 10] },
  { code: '+90', country: 'TR', flag: '🇹🇷', label: 'Türkei', digits: [10, 10] },
  { code: '+381', country: 'RS', flag: '🇷🇸', label: 'Serbien', digits: [8, 9] },
  { code: '+383', country: 'XK', flag: '🇽🇰', label: 'Kosovo', digits: [8, 8] },
  { code: '+355', country: 'AL', flag: '🇦🇱', label: 'Albanien', digits: [8, 9] },
  { code: '+387', country: 'BA', flag: '🇧🇦', label: 'Bosnien', digits: [8, 8] },
  { code: '+385', country: 'HR', flag: '🇭🇷', label: 'Kroatien', digits: [8, 9] },
  { code: '+351', country: 'PT', flag: '🇵🇹', label: 'Portugal', digits: [9, 9] },
  { code: '+34', country: 'ES', flag: '🇪🇸', label: 'Spanien', digits: [9, 9] },
];

function parsePhoneValue(value) {
  if (!value) return { countryCode: '+41', localNumber: '' };
  const str = String(value).trim();

  for (const cc of COUNTRY_CODES.slice().sort((a, b) => b.code.length - a.code.length)) {
    if (str.startsWith(cc.code)) {
      return { countryCode: cc.code, localNumber: str.slice(cc.code.length).replace(/^\s+/, '') };
    }
  }

  if (str.startsWith('+')) {
    const digits = str.replace(/[^\d]/g, '');
    if (digits.startsWith('41')) return { countryCode: '+41', localNumber: digits.slice(2) };
    if (digits.startsWith('49')) return { countryCode: '+49', localNumber: digits.slice(2) };
    if (digits.startsWith('43')) return { countryCode: '+43', localNumber: digits.slice(2) };
    return { countryCode: '+41', localNumber: digits };
  }

  if (str.startsWith('0')) {
    return { countryCode: '+41', localNumber: str.slice(1) };
  }

  return { countryCode: '+41', localNumber: str.replace(/[^\d\s]/g, '') };
}

/** Validate a full phone string (e.g. "+41791234567"). Returns { valid, message }. */
export function validatePhone(fullValue) {
  if (!fullValue) return { valid: true, message: '' }; // empty = no error (use required attr separately)
  const parsed = parsePhoneValue(fullValue);
  const cc = COUNTRY_CODES.find(c => c.code === parsed.countryCode);
  if (!cc) return { valid: true, message: '' };
  const digitCount = parsed.localNumber.replace(/\s/g, '').length;
  if (digitCount === 0) return { valid: true, message: '' };
  const [min, max] = cc.digits;
  if (digitCount < min) return { valid: false, message: `Zu kurz — ${cc.country} braucht ${min === max ? min : `${min}-${max}`} Ziffern (aktuell ${digitCount})` };
  if (digitCount > max) return { valid: false, message: `Zu lang — ${cc.country} braucht ${min === max ? min : `${min}-${max}`} Ziffern (aktuell ${digitCount})` };
  return { valid: true, message: '' };
}

export function PhoneInput({
  value,
  onChange,
  placeholder,
  className = '',
  disabled = false,
  id,
  'data-testid': dataTestId,
  isDark = false,
  showValidation = true,
}) {
  const parsed = parsePhoneValue(value);
  const [countryCode, setCountryCode] = useState(parsed.countryCode);
  const [localNumber, setLocalNumber] = useState(parsed.localNumber);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const p = parsePhoneValue(value);
    setCountryCode(p.countryCode);
    setLocalNumber(p.localNumber);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const emitChange = (cc, num) => {
    const full = num ? `${cc}${num.replace(/\s/g, '')}` : '';
    if (onChange) {
      if (typeof onChange === 'function') {
        // Support both direct value and event-like onChange
        const syntheticEvent = { target: { value: full, name: id } };
        onChange(syntheticEvent);
      }
    }
  };

  const handleLocalChange = (e) => {
    let raw = e.target.value;
    // Auto-remove leading 0
    if (raw.startsWith('0')) {
      raw = raw.slice(1);
    }
    // Only allow digits and spaces
    raw = raw.replace(/[^\d\s]/g, '');
    setLocalNumber(raw);
    emitChange(countryCode, raw);
  };

  const handleCountrySelect = (cc) => {
    setCountryCode(cc);
    setDropdownOpen(false);
    emitChange(cc, localNumber);
  };

  const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0];
  const digitCount = localNumber.replace(/\s/g, '').length;
  const [min, max] = selectedCountry.digits;
  const isValid = digitCount === 0 || (digitCount >= min && digitCount <= max);
  const isTooShort = digitCount > 0 && digitCount < min;
  const isTooLong = digitCount > max;
  const isComplete = digitCount >= min && digitCount <= max;

  const darkBg = isDark ? 'bg-white/10 border-white/20 text-white' : '';
  const darkDropdown = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const darkItem = isDark ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-100';

  return (
    <div>
      <div className={`flex items-center gap-0 ${className}`} data-testid={dataTestId || 'phone-input'}>
      {/* Country Code Selector */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setDropdownOpen(!dropdownOpen)}
          disabled={disabled}
          className={`flex items-center gap-1 px-2 h-10 border rounded-l-md text-sm whitespace-nowrap ${darkBg || 'bg-gray-50 border-gray-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          data-testid="phone-country-selector"
        >
          <span>{selectedCountry.flag}</span>
          <span className="font-mono text-xs">{countryCode}</span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </button>

        {dropdownOpen && (
          <div className={`absolute z-50 top-full left-0 mt-1 w-56 max-h-60 overflow-y-auto border rounded-md shadow-lg ${darkDropdown}`}>
            {COUNTRY_CODES.map(cc => (
              <button
                key={cc.code}
                type="button"
                onClick={() => handleCountrySelect(cc.code)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${darkItem} ${cc.code === countryCode ? 'font-semibold' : ''}`}
              >
                <span>{cc.flag}</span>
                <span className="flex-1">{cc.label}</span>
                <span className="font-mono text-xs opacity-60">{cc.code}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Local Number Input */}
      <Input
        id={id}
        type="tel"
        value={localNumber}
        onChange={handleLocalChange}
        placeholder={placeholder || '78 123 45 67'}
        disabled={disabled}
        className={`rounded-l-none border-l-0 flex-1 ${isDark ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' : ''} ${showValidation && !isValid ? 'border-red-400 focus-visible:ring-red-400' : ''} ${showValidation && isComplete ? 'border-green-400 focus-visible:ring-green-400' : ''}`}
        data-testid="phone-local-input"
      />
      {/* Validation icon */}
      {showValidation && digitCount > 0 && (
        <div className="flex items-center ml-2">
          {isComplete ? (
            <CheckCircle2 className="h-4 w-4 text-green-500" data-testid="phone-valid-icon" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-400" data-testid="phone-invalid-icon" />
          )}
        </div>
      )}
    </div>
    {/* Validation message */}
    {showValidation && !isValid && digitCount > 0 && (
      <p className={`text-xs mt-1 ${isDark ? 'text-red-300' : 'text-red-500'}`} data-testid="phone-validation-msg">
        {isTooShort ? `Zu kurz` : 'Zu lang'} — {selectedCountry.country} braucht {min === max ? min : `${min}-${max}`} Ziffern (aktuell {digitCount})
      </p>
    )}
    </div>
  );
}
